import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import appStoreBadge from "../Download_on_the_App_Store_Badge_US.svg";
import googlePlayBadge from "../google-play-badge-logo-svgrepo-com.svg";
import screenshot1 from "../assets/jackpotSingleBook.png";
import screenshot2 from "../assets/jackpotQuestion.png";
import vegas from "../vegas.jpg";
import "@fontsource/oswald";

const CHECKOUT_ENDPOINT =
    process.env.REACT_APP_CONCIERGE_CHECKOUT_ENDPOINT ||
    "https://l7qmpdzu54.execute-api.us-west-1.amazonaws.com/prod/conciergeServiceEmail";

const initialForm = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    startDate: "",
    endDate: "",
    partySize: "",
    preferredContact: "text",
    agreeToTerms: false,
    Country: "",
};

const ConciergeService = () => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [message, setMessage] = useState("");
    const [showPromo, setShowPromo] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [promoStatus, setPromoStatus] = useState("idle");
    const [promoMessage, setPromoMessage] = useState("");

    const update = (field) => (event) => {
        const value = event.target.type === "checkbox"
            ? event.target.checked
            : event.target.value;
        setForm((previous) => ({ ...previous, [field]: value }));
        setErrors((previous) => ({ ...previous, [field]: "" }));
        setMessage("");
    };

    const validate = () => {
        const next = {};
        ["firstName", "lastName", "email", "phone", "startDate", "endDate", "partySize", "preferredContact"].forEach((field) => {
            if (!String(form[field] || "").trim()) next[field] = "This field is required.";
        });
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            next.email = "Please enter a valid email address.";
        }
        if (form.partySize && Number(form.partySize) < 1) {
            next.partySize = "Party size must be at least 1.";
        }
        if (form.startDate && form.endDate && form.endDate < form.startDate) {
            next.endDate = "End date must be on or after the start date.";
        }
        if (!form.agreeToTerms) next.agreeToTerms = "You must agree to the Terms of Use.";
        return next;
    };

    const startCheckout = async (event, appliedPromoCode = null, rethrowError = false) => {
        event?.preventDefault();
        if (form.Country) return;

        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            setMessage("Please complete the required fields.");
            if (rethrowError) throw new Error("Complete the required booking fields before applying your code.");
            return;
        }

        setStatus("loading");
        setMessage("");

        try {
            const response = await fetch(CHECKOUT_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    productType: "concierge_support",
                    productId: "prod_VL58ETQrNXxjie",
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim(),
                    startDate: form.startDate,
                    endDate: form.endDate,
                    visitDates: `${form.startDate} to ${form.endDate}`,
                    partySize: Number(form.partySize),
                    preferredContact: form.preferredContact,
                    promoCode: appliedPromoCode,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.url) throw new Error(data.message || "Checkout could not be started.");
            window.location.assign(data.url);
        } catch (error) {
            console.error("Concierge checkout error", error);
            setStatus("error");
            setMessage(error.message || "We couldn't start checkout. Please try again or contact info@jackpotconcierge.com.");
            if (rethrowError) throw error;
        }
    };

    const updatePromoCode = (event) => {
        setPromoCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 20));
        setPromoStatus("idle");
        setPromoMessage("");
    };

    const applyPromoCode = async () => {
        const normalizedCode = promoCode.trim().toUpperCase();
        if (!normalizedCode) {
            setPromoStatus("error");
            setPromoMessage("Please enter a promo code.");
            return;
        }

        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            setPromoStatus("error");
            setPromoMessage("Complete the required booking fields before applying your code.");
            return;
        }

        setPromoStatus("checking");
        setPromoMessage("Applying promo code...");
        try {
            await startCheckout(null, normalizedCode, true);
        } catch (error) {
            setPromoStatus("error");
            setPromoMessage(error.message || "Failed to apply promo code.");
        }
    };

    const inputClass = (field) =>
        `mt-1 w-full rounded-md border bg-gray-50 px-3 py-2 text-gray-900 outline-none focus:ring-2 focus:ring-[#b42520] ${
            errors[field] ? "border-red-500" : "border-gray-300"
        }`;

    const ErrorMessage = ({ field }) => errors[field] ? (
        <p className="mt-1 text-xs font-semibold text-red-600">{errors[field]}</p>
    ) : null;

    return (
        <div className="min-h-screen bg-cover bg-center text-white" style={{ backgroundImage: `url(${vegas})` }}>
            <div className="min-h-screen bg-black bg-opacity-80">
                <Header />
                <main className="animate-fade-in mx-auto max-w-6xl space-y-10 px-6 py-12" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <section className="rounded-xl border border-white/20 bg-white/10 p-6 text-white shadow-xl md:p-10">
                        <h1 className="text-center text-4xl font-bold text-white">Concierge Support</h1>
                        <div className="mt-6 space-y-4 text-lg !text-white">
                            <p className="text-white">Enjoy a seamless Las Vegas experience with dedicated concierge support throughout your stay. Our team is here to help with last minute requests, insider recommendations, reservations, and everything in between.</p>
                            <p className="text-white">We'll leverage our local connections and partnerships to help you access exclusive perks, preferred pricing, and VIP opportunities whenever available. Whether you need a dinner reservation, show tickets, nightlife recommendations, or help with a last minute request, Jackpot Concierge is just a message away.</p>
                            <ul className="list-disc space-y-2 pl-6 font-semibold text-white">
                                <li className="text-white">Dedicated concierge support during your stay</li>
                                <li className="text-white">Assistance with reservations and last minute requests</li>
                                <li className="text-white">Expert recommendations from Las Vegas insiders</li>
                                <li className="text-white">Access to exclusive perks and partner benefits</li>
                                <li className="text-white">VIP assistance for dining, nightlife, shows, and experiences</li>
                            </ul>
                            <p className="text-white"><strong className="text-white">Prefer to book concierge services on the go?</strong> You can also access concierge support through the Jackpot Concierge mobile app, making it easy to request recommendations, reservations, and assistance anytime during your stay.</p>
                        </div>

                        <div className="mt-8 flex flex-wrap justify-center gap-4">
                            <a href="https://apps.apple.com/us/app/jackpot-concierge/id6642683429" target="_blank" rel="noopener noreferrer">
                                <img src={appStoreBadge} alt="Download on the App Store" className="h-12" />
                            </a>
                            <a href="https://play.google.com/store/apps/details?id=com.jackpotconcierge.jackpotconciergeapp" target="_blank" rel="noopener noreferrer">
                                <img src={googlePlayBadge} alt="Get it on Google Play" className="h-12" />
                            </a>
                        </div>

                        <div className="mt-8 flex flex-wrap justify-center gap-4">
                            <div className="w-[180px] rounded-lg border-2 border-gray-300 bg-white p-1 sm:w-[220px]">
                                <img src={screenshot1} alt="Jackpot Concierge mobile app booking screen" className="h-full w-full object-contain" />
                            </div>
                            <div className="w-[180px] rounded-lg border-2 border-gray-300 bg-white p-1 sm:w-[220px]">
                                <img src={screenshot2} alt="Jackpot Concierge mobile app support screen" className="h-full w-full object-contain" />
                            </div>
                        </div>
                    </section>

                    <section className="mx-auto max-w-3xl rounded-xl bg-white p-6 text-gray-900 shadow-xl md:p-10">
                        <h2 className="text-center text-3xl font-bold">Book Concierge Support</h2>
                        <p className="mt-2 text-center text-gray-600">Unlimited support during your stay · $49.99</p>

                        <form className="mt-8" onSubmit={startCheckout} noValidate>
                            <div className="absolute -left-[9999px]" aria-hidden="true">
                                <label htmlFor="concierge-country">Country</label>
                                <input id="concierge-country" value={form.Country} onChange={update("Country")} tabIndex="-1" autoComplete="off" />
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <label className="text-sm font-medium">First Name *
                                    <input type="text" value={form.firstName} onChange={update("firstName")} className={inputClass("firstName")} autoComplete="given-name" />
                                    <ErrorMessage field="firstName" />
                                </label>
                                <label className="text-sm font-medium">Last Name *
                                    <input type="text" value={form.lastName} onChange={update("lastName")} className={inputClass("lastName")} autoComplete="family-name" />
                                    <ErrorMessage field="lastName" />
                                </label>
                                <label className="text-sm font-medium">Email *
                                    <input type="email" value={form.email} onChange={update("email")} className={inputClass("email")} autoComplete="email" />
                                    <ErrorMessage field="email" />
                                </label>
                                <label className="text-sm font-medium">Phone Number *
                                    <input type="tel" value={form.phone} onChange={update("phone")} className={inputClass("phone")} autoComplete="tel" />
                                    <ErrorMessage field="phone" />
                                </label>
                                <label className="text-sm font-medium">Trip Start Date *
                                    <input type="date" value={form.startDate} onChange={update("startDate")} className={inputClass("startDate")} />
                                    <ErrorMessage field="startDate" />
                                </label>
                                <label className="text-sm font-medium">Trip End Date *
                                    <input type="date" value={form.endDate} min={form.startDate} onChange={update("endDate")} className={inputClass("endDate")} />
                                    <ErrorMessage field="endDate" />
                                </label>
                                <label className="text-sm font-medium md:col-span-2">How many people are in your party? *
                                    <input type="number" min="1" value={form.partySize} onChange={update("partySize")} className={inputClass("partySize")} />
                                    <ErrorMessage field="partySize" />
                                </label>
                            </div>

                            <fieldset className="mt-5">
                                <legend className="text-sm font-medium">How would you like to be contacted? *</legend>
                                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    {["phone", "email", "text"].map((method) => (
                                        <button key={method} type="button" onClick={() => setForm((previous) => ({ ...previous, preferredContact: method }))} className={`rounded-md border px-4 py-2 text-sm font-semibold capitalize ${form.preferredContact === method ? "border-[#b42520] bg-[#b42520] text-white" : "border-gray-300 bg-white"}`}>
                                            {method === "phone" ? "Phone Call" : method}
                                        </button>
                                    ))}
                                </div>
                                <ErrorMessage field="preferredContact" />
                            </fieldset>

                            <label className="mt-6 flex items-start gap-3 text-sm">
                                <input type="checkbox" checked={form.agreeToTerms} onChange={update("agreeToTerms")} className="mt-1 h-4 w-4 accent-[#b42520]" />
                                <span>I agree to the Jackpot Concierge <Link to="/terms-of-use" className="font-semibold text-[#b42520] underline">Terms of Use</Link>. *</span>
                            </label>
                            <ErrorMessage field="agreeToTerms" />

                            {message && <p className={`mt-4 text-center text-sm font-semibold ${status === "error" ? "text-red-600" : "text-gray-700"}`} role="alert">{message}</p>}

                            <div className="mt-5 text-center">
                                <button type="button" onClick={() => setShowPromo((visible) => !visible)} className="font-semibold text-[#b42520] underline underline-offset-2">
                                    Enter promo code
                                </button>
                                {showPromo && (
                                    <div className="mx-auto mt-3 max-w-md">
                                        <div className="flex gap-2">
                                            <input type="text" value={promoCode} onChange={updatePromoCode} placeholder="Enter promo code" maxLength="20" className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 uppercase" />
                                            <button type="button" onClick={applyPromoCode} disabled={promoStatus === "checking" || status === "loading"} className="rounded-md bg-[#b42520] px-4 py-2 font-semibold text-white hover:bg-[#941c18] disabled:opacity-60">
                                                {promoStatus === "checking" ? "Checking..." : "Apply"}
                                            </button>
                                        </div>
                                        {promoMessage && <p className={`mt-2 text-sm font-semibold ${promoStatus === "error" ? "text-red-600" : "text-green-600"}`} role="status">{promoMessage}</p>}
                                    </div>
                                )}
                            </div>

                            <button type="submit" disabled={status === "loading"} className="mt-6 w-full rounded-lg bg-[#b42520] px-6 py-4 text-xl font-extrabold text-white transition hover:bg-[#941c18] disabled:cursor-wait disabled:opacity-70">
                                {status === "loading" ? "Opening Secure Checkout..." : "Book Concierge Support · $49.99"}
                            </button>
                            <p className="mt-2 text-center text-xs text-gray-500">Secure payment powered by Stripe</p>
                        </form>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default ConciergeService;
